import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const HOUR_START = 7;
const HOUR_END = 21; // exclusive; 21 means last row covers 20:00-21:00
const ROW_HEIGHT = 30;
const HEADER_HEIGHT = 44; // başlık hücresi sabit yükseklik: bloklar satırlarla hizalı kalsın

const pad = (n) => String(n).padStart(2, "0");

const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 Sun - 6 Sat
  const offset = day === 0 ? -6 : 1 - day; // make Monday the first day
  d.setDate(d.getDate() + offset);
  return d;
};

const NARROW_QUERY = "(max-width: 700px)";

// Dar ekranda (telefon) 7 sütun okunmaz; tek günlük görünüme geçilir.
const useIsNarrow = () => {
  const get = () =>
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia(NARROW_QUERY).matches : false;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return narrow;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const WeekGrid = ({
  weekStart,
  reservations,
  currentUserId,
  onSlotClick,
  t,
}) => {
  const weekDays = useMemo(() => {
    const ws = startOfWeek(weekStart);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(ws);
      d.setDate(ws.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const narrow = useIsNarrow();
  // Seçili gün (dar ekran): hafta içindeyse bugün, değilse haftanın ilk günü
  const [dayIdx, setDayIdx] = useState(0);
  useEffect(() => {
    const today = new Date();
    const idx = weekDays.findIndex((d) => sameDay(d, today));
    setDayIdx(idx >= 0 ? idx : 0);
  }, [weekDays]);

  const visibleIdx = useMemo(
    () => (narrow ? [Math.min(dayIdx, weekDays.length - 1)] : weekDays.map((_, i) => i)),
    [narrow, dayIdx, weekDays]
  );
  const cols = visibleIdx.length;
  const labelCol = narrow ? 48 : 60;

  const hours = useMemo(() => {
    const out = [];
    for (let h = HOUR_START; h < HOUR_END; h += 1) out.push(h);
    return out;
  }, []);

  const dayLabels = t
    ? [
        t("fleet.days.mon"),
        t("fleet.days.tue"),
        t("fleet.days.wed"),
        t("fleet.days.thu"),
        t("fleet.days.fri"),
        t("fleet.days.sat"),
        t("fleet.days.sun"),
      ]
    : ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

  // Build block positions per day
  const blocksByDay = useMemo(() => {
    const map = weekDays.map(() => []);
    for (const r of reservations) {
      const start = new Date(r.starts_at);
      const end = new Date(r.ends_at);
      for (let i = 0; i < weekDays.length; i += 1) {
        const day = weekDays[i];
        const dayStart = new Date(day);
        dayStart.setHours(HOUR_START, 0, 0, 0);
        const dayEnd = new Date(day);
        dayEnd.setHours(HOUR_END, 0, 0, 0);
        if (end <= dayStart || start >= dayEnd) continue;
        const s = start < dayStart ? dayStart : start;
        const e = end > dayEnd ? dayEnd : end;
        const topMinutes = (s - dayStart) / 60000;
        const durationMinutes = (e - s) / 60000;
        map[i].push({
          reservation: r,
          top: (topMinutes / 60) * ROW_HEIGHT,
          height: Math.max(14, (durationMinutes / 60) * ROW_HEIGHT - 2),
        });
      }
    }
    return map;
  }, [reservations, weekDays]);

  const now = new Date();

  // Klavye erişimi: tek tab durağı (roving tabindex) + ok tuşlarıyla gezinme, Enter/Boşluk ile seçim.
  const gridRef = useRef(null);
  const [focusPos, setFocusPos] = useState({ r: 0, c: 0 });
  const shouldFocusRef = useRef(false);
  const lastRow = hours.length - 1;
  const lastCol = visibleIdx.length - 1;

  useEffect(() => {
    // Görünür sütun sayısı değişince (dar/geniş geçiş, gün seçimi) odak sınır dışında kalmasın
    setFocusPos((p) => ({ r: Math.min(p.r, lastRow), c: Math.min(p.c, lastCol) }));
  }, [lastRow, lastCol]);

  useEffect(() => {
    if (!shouldFocusRef.current) return;
    shouldFocusRef.current = false;
    gridRef.current
      ?.querySelector(`[data-slot="${focusPos.r}-${focusPos.c}"]`)
      ?.focus();
  }, [focusPos]);

  const onGridKeyDown = useCallback(
    (e) => {
      const { r, c } = focusPos;
      let next = null;
      if (e.key === "ArrowDown") next = { r: Math.min(lastRow, r + 1), c };
      else if (e.key === "ArrowUp") next = { r: Math.max(0, r - 1), c };
      else if (e.key === "ArrowRight") next = { r, c: Math.min(lastCol, c + 1) };
      else if (e.key === "ArrowLeft") next = { r, c: Math.max(0, c - 1) };
      else if (e.key === "Home") next = { r: 0, c };
      else if (e.key === "End") next = { r: lastRow, c };
      if (!next) return;
      e.preventDefault();
      shouldFocusRef.current = true;
      setFocusPos(next);
    },
    [focusPos, lastRow, lastCol]
  );

  return (
    <>
    {narrow && (
      <div className="week-day-strip" role="tablist">
        {weekDays.map((d, i) => (
          <button
            key={`s-${i}`}
            type="button"
            role="tab"
            aria-selected={i === visibleIdx[0]}
            className={`week-day-chip${i === visibleIdx[0] ? " active" : ""}${
              sameDay(d, now) ? " today" : ""
            }`}
            onClick={() => setDayIdx(i)}
          >
            <span>{dayLabels[i]}</span>
            <small>
              {pad(d.getDate())}.{pad(d.getMonth() + 1)}
            </small>
            {blocksByDay[i].length > 0 && <i className="week-day-dot" aria-hidden="true" />}
          </button>
        ))}
      </div>
    )}
    <div
      ref={gridRef}
      role="grid"
      aria-label={t ? t("fleet.weekGridLabel") : undefined}
      onKeyDown={onGridKeyDown}
      className={`week-grid${narrow ? " week-grid--day" : ""}`}
      style={{
        position: "relative",
        gridTemplateColumns: `${labelCol}px repeat(${cols}, 1fr)`,
        minWidth: narrow ? 0 : undefined,
      }}
    >
      {/* Header row */}
      <div className="week-grid-cell header" style={{ height: HEADER_HEIGHT }} />
      {visibleIdx.map((i) => {
        const d = weekDays[i];
        return (
          <div
            key={`h-${i}`}
            className={`week-grid-cell header${sameDay(d, now) ? " today" : ""}`}
            style={{ height: HEADER_HEIGHT }}
          >
            {dayLabels[i]}
            <br />
            <small>
              {pad(d.getDate())}.{pad(d.getMonth() + 1)}
            </small>
          </div>
        );
      })}

      {/* Rows */}
      {hours.map((h, hi) => (
        <React.Fragment key={`row-${h}`}>
          <div
            className="week-grid-cell hour-label"
            style={{ height: ROW_HEIGHT }}
          >
            {pad(h)}:00
          </div>
          {visibleIdx.map((di, vi) => {
            const d = weekDays[di];
            const slot = new Date(d);
            slot.setHours(h, 0, 0, 0);
            const isPast = slot.getTime() + 3600000 <= now.getTime(); // geçmiş saat dilimine rezervasyon açılmaz
            const activate = () => {
              if (!onSlotClick || isPast) return;
              onSlotClick(slot);
            };
            return (
              <div
                key={`c-${h}-${di}`}
                role="gridcell"
                data-slot={`${hi}-${vi}`}
                tabIndex={focusPos.r === hi && focusPos.c === vi ? 0 : -1}
                aria-disabled={isPast || undefined}
                aria-label={`${dayLabels[di]} ${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(h)}:00`}
                className={`week-grid-cell day-col${isPast ? " past" : ""}`}
                style={{ height: ROW_HEIGHT }}
                onFocus={() => {
                  if (focusPos.r !== hi || focusPos.c !== vi) setFocusPos({ r: hi, c: vi });
                }}
                onClick={activate}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    activate();
                  }
                }}
              />
            );
          })}
        </React.Fragment>
      ))}

      {/* Reservation blocks */}
      {visibleIdx.map((di, vi) => {
        const blocks = blocksByDay[di];
        return (
        <div
          key={`col-${di}`}
          style={{
            position: "absolute",
            top: HEADER_HEIGHT, // başlığın hemen altı
            left: `calc(${labelCol}px + ${vi} * ((100% - ${labelCol}px) / ${cols}))`,
            width: `calc((100% - ${labelCol}px) / ${cols})`,
            height: (HOUR_END - HOUR_START) * ROW_HEIGHT,
            pointerEvents: "none",
          }}
        >
          {blocks.map((b, bi) => {
            const r = b.reservation;
            const isOwn = r.user_id === currentUserId;
            const cls = `reservation-block ${r.status}${isOwn ? " own" : ""}`;
            return (
              <div
                key={`b-${di}-${bi}`}
                className={cls}
                style={{ top: b.top, height: b.height }}
                title={`${r.owner_name || ""} ${r.owner_surname || ""}${
                  r.reason ? " — " + r.reason : ""
                }`}
              >
                <div>
                  {r.owner_name} {r.owner_surname}
                </div>
                {r.reason && <small>{r.reason}</small>}
              </div>
            );
          })}
        </div>
        );
      })}
    </div>
    </>
  );
};

export { startOfWeek };
export default WeekGrid;
